import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ShoppingCart, Plus, Package, Users, LayoutGrid, Trash2, X, Search, Camera, Receipt, Save,
  Minus, Image as ImageIcon, BarChart3, Settings, Printer, Clock, ArrowUpRight, Edit3,
  FileText, Download, AlertTriangle, Ban, UserCheck, Share2, Copy, AlertOctagon, RefreshCw, Truck,
  RotateCcw, ChevronLeft, ChevronRight, ScanBarcode, UserCircle2, Layers, Lock, LogIn, Menu, LogOut,
  HelpCircle, CheckSquare, Square, MapPin, Phone, PauseCircle, PlayCircle, AlertCircle, Banknote,
  CreditCard, Smartphone, BookOpen, Crown, Unlock, Globe, Wallet, History, FileCheck, Boxes, PenLine,
  CreditCard as CardIcon, Store, ArrowRightLeft, UserCog, Briefcase, FileOutput, Megaphone, UserPlus,
  Link, MonitorSmartphone, Contact, FileMinus } from 'lucide-react';
import { apiCall, generateId, formatPrice, formatDate, compressImage,
  base64ToFile, escapeHtml, printFullHtml, printHtmlDiv, printRawHtml, printBarcodeDiv } from '../utils/utils.js';

const BarcodeModal = ({ product, onClose, getProductPrice, onSaveBarcode }) => {
    const [labelQty, setLabelQty] = useState(1);
    const [barcodeVal, setBarcodeVal] = useState(product?.barcode || '');
    const [barcodeError, setBarcodeError] = useState('');

    // Max chars that fit cleanly on 1.5" at 12pt bold
    const MAX_NAME_CHARS = 22;
    const productName = product?.name || '';
    const displayName = productName.length > MAX_NAME_CHARS ? productName.slice(0, MAX_NAME_CHARS) : productName;
    const nameWarning = productName.length > MAX_NAME_CHARS;

    useEffect(() => {
        const timer = setTimeout(() => renderBarcode(barcodeVal), 100);
        return () => clearTimeout(timer);
    }, [barcodeVal]);

    const renderBarcode = (val) => {
        if (!val) return;
        const el = document.getElementById('barcode-canvas');
        if (!el || !window.JsBarcode) return;
        try {
            // Natural size — no height constraint — barcode determines its own height
            window.JsBarcode(el, val, {
                format: "CODE128",
                width: 2,
                height: 60,
                displayValue: true,
                fontSize: 12,
                textMargin: 3,
                margin: 4,
                background: "#ffffff",
                lineColor: "#000000",
            });
            setBarcodeError('');
        } catch(e) { setBarcodeError('Invalid barcode — use numbers/letters only, no special characters'); }
    };

    const handleGenerate = () => {
        const newCode = '27' + Date.now().toString().slice(-8);
        setBarcodeVal(newCode);
    };

    const handlePrint = () => {
        if (!barcodeVal) return;
        if (!window.JsBarcode) { alert('JsBarcode not loaded'); return; }

        // Render a fresh high-res SVG for print
        const uid = `pbc-${Date.now()}`;
        const tmp = document.createElement('div');
        tmp.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:144px;';
        tmp.innerHTML = `<svg id="${uid}"></svg>`;
        document.body.appendChild(tmp);

        try {
            window.JsBarcode(`#${uid}`, barcodeVal, {
                format: "CODE128",
                width: 1.8,       // bar width in px — keeps it machine-readable at 1.5in
                height: 55,       // natural height, not constrained
                displayValue: true,
                fontSize: 10,
                textMargin: 2,
                margin: 2,
                background: "#ffffff",
                lineColor: "#000000",
            });
        } catch(e) {
            document.body.removeChild(tmp);
            setBarcodeError('Invalid barcode value');
            return;
        }

        const svgEl = document.getElementById(uid);
        // Force explicit width/height so it doesn't overflow the label
        svgEl.setAttribute('width', '1.44in');
        svgEl.removeAttribute('height');   // let height be natural
        const svgHtml = svgEl.outerHTML;
        document.body.removeChild(tmp);

        const price = formatPrice(getProductPrice(product));

        let labelsHtml = '';
        for (let i = 0; i < labelQty; i++) {
            labelsHtml += `<div class="label">
                <div class="name">${escapeHtml(displayName)}</div>
                <div class="price">${price}</div>
                <div class="bc">${svgHtml}</div>
            </div>`;
        }

        printFullHtml(`<!DOCTYPE html>
<html><head><title>Barcode Labels</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  @page{size:1.5in 1in;margin:0;}
  html,body{width:1.5in;background:#fff;font-family:Arial,Helvetica,sans-serif;}
  .label{
width:1.5in;
height:1in;
display:flex;
flex-direction:column;
align-items:center;
justify-content:flex-start;
padding:4pt 2pt 2pt 2pt;
overflow:hidden;
page-break-after:always;
break-after:page;
  }
  .name{
font-size:11pt;
font-weight:900;
text-align:center;
white-space:nowrap;
overflow:hidden;
text-overflow:ellipsis;
width:1.44in;
line-height:1.2;
margin-bottom:0;
  }
  .price{
font-size:19pt;
font-weight:900;
text-align:center;
line-height:1.1;
margin-bottom:1pt;
  }
  .bc{
width:1.44in;
display:flex;
align-items:center;
justify-content:center;
flex:1;
  }
  /* SVG barcode: full width, auto height — scanner-friendly */
  .bc svg{
width:1.44in!important;
height:auto!important;
display:block;
  }
</style>
</head><body>
${labelsHtml}
</body></html>`, 700);
    };

    if (!product) return null;
    return (
        <div className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-xl shadow-lg border border-[#e1e3e5] p-5">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-base text-[#1a1a1a] flex items-center gap-2">
                        <ScanBarcode size={18} className="text-[#008060]"/> Barcode Label
                    </h3>
                    <button onClick={onClose} className="text-[#5c5f62] hover:text-[#1a1a1a]"><X size={20}/></button>
                </div>

                {/* Barcode value input - any chars, max 10, with live counter */}
                <div className="flex gap-2 mb-2">
                    <div className="relative flex-1">
                        <input
                            value={barcodeVal}
                            onChange={e => setBarcodeVal(e.target.value.slice(0, 10))}
                            placeholder="Enter or scan barcode…"
                            maxLength={10}
                            className="w-full p-2 pr-10 border border-[#c9cccf] rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#008060] font-mono tracking-wider"
                        />
                        <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold ${barcodeVal.length >= 10 ? 'text-[#d82c0d]' : barcodeVal.length >= 7 ? 'text-[#b98900]' : 'text-[#5c5f62]'}`}>
                            {barcodeVal.length}/10
                        </span>
                    </div>
                    <button onClick={handleGenerate}
                        className="bg-[#f6f7f8] border border-[#c9cccf] text-[#1a1a1a] px-3 py-2 rounded-lg text-xs font-semibold hover:bg-[#ebebeb] whitespace-nowrap">
                        Auto
                    </button>
                </div>
                {barcodeVal.length >= 10 && (
                    <div className="flex items-center gap-2 bg-[#fbeae5] border border-[#d82c0d]/30 text-[#d82c0d] text-xs rounded-lg px-3 py-1.5 mb-2">
                        <AlertTriangle size={12} className="shrink-0"/>
                        <span>Maximum 10 characters reached — label is at full capacity</span>
                    </div>
                )}

                {barcodeError && (
                    <div className="flex items-start gap-2 bg-[#fbeae5] border border-[#d82c0d]/30 text-[#d82c0d] text-xs rounded-lg px-3 py-2 mb-2">
                        <AlertTriangle size={13} className="mt-0.5 shrink-0"/>
                        <span>{barcodeError}</span>
                    </div>
                )}

                {barcodeVal && barcodeVal !== product.barcode && !barcodeError && (
                    <button onClick={() => onSaveBarcode(product.id, barcodeVal)}
                        className="w-full mb-2 bg-[#008060] text-white text-xs py-2 rounded-lg font-semibold hover:bg-[#006e52]">
                        Save Barcode to Product
                    </button>
                )}

                {/* Name truncation warning */}
                {nameWarning && (
                    <div className="flex items-start gap-2 bg-[#fff5ea] border border-[#b98900]/40 text-[#b98900] text-xs rounded-lg px-3 py-2 mb-3">
                        <AlertTriangle size={13} className="mt-0.5 shrink-0"/>
                        <span>Name is {productName.length} chars — only first {MAX_NAME_CHARS} will print. Printed: <strong>"{displayName}"</strong></span>
                    </div>
                )}

                {/* PREVIEW — no forced height on barcode, it renders at natural size */}
                <div style={{
                    border: '2px dashed rgba(0,128,96,0.3)',
                    borderRadius: '10px',
                    background: '#fff',
                    padding: '8px 8px 6px',
                    textAlign: 'center',
                    marginBottom: '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <div style={{
                        fontFamily: 'Arial,sans-serif',
                        fontWeight: 900,
                        fontSize: '16px',
                        lineHeight: 1.2,
                        marginBottom: '1px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: '#000',
                    }}>
                        {displayName}
                    </div>
                    <div style={{
                        fontFamily: 'Arial,sans-serif',
                        fontWeight: 900,
                        fontSize: '28px',
                        lineHeight: 1.05,
                        marginBottom: '4px',
                        color: '#000',
                    }}>
                        {formatPrice(getProductPrice(product))}
                    </div>
                    {barcodeVal && !barcodeError
                        ? <svg id="barcode-canvas" style={{display:'block', width:'100%', height:'auto', margin:'0 auto'}}></svg>
                        : !barcodeVal && <p style={{fontSize:'11px',color:'#5c5f62',fontStyle:'italic',padding:'12px 0'}}>Enter a barcode or tap Auto to generate one</p>
                    }
                </div>

                {/* Quantity + Print */}
                <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold text-[#5c5f62] whitespace-nowrap">Qty:</label>
                    <div className="flex items-center bg-[#f6f7f8] border border-[#c9cccf] rounded-lg">
                        <button onClick={() => setLabelQty(q => Math.max(1, q-1))} className="px-2.5 py-1.5 text-[#5c5f62] hover:text-[#1a1a1a]"><Minus size={13}/></button>
                        <span className="px-3 font-bold text-sm min-w-[28px] text-center">{labelQty}</span>
                        <button onClick={() => setLabelQty(q => Math.min(500, q+1))} className="px-2.5 py-1.5 text-[#5c5f62] hover:text-[#1a1a1a]"><Plus size={13}/></button>
                    </div>
                    <button onClick={handlePrint} disabled={!barcodeVal || !!barcodeError}
                        className="flex-1 bg-[#1a1a1a] text-white py-2.5 rounded-lg font-semibold flex justify-center items-center gap-2 hover:bg-black disabled:opacity-40 text-sm transition-colors">
                        <Printer size={15}/> Print {labelQty > 1 ? `${labelQty} Labels` : 'Label'}
                    </button>
                </div>
                <p className="text-[10px] text-[#5c5f62] text-center mt-2">Zebra LP2824 · 1.5" × 1" · CODE128</p>
            </div>
        </div>
    );
};

export default BarcodeModal;
